import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Search, Loader2, Download, MapPin, Users, DollarSign,
  AlertTriangle, Home, Building2, X, Layers, ListFilter, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { api } from "../lib/api";
import { exportCsv } from "../lib/csv";
import { useAuth } from "@/context/AuthContext";

// Fix Leaflet's default-marker asset paths so pins actually show up
// (webpack strips the require() shim so we point at the CDN copies).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const money = (n) =>
  n == null ? "—" : `$${Math.round(Number(n)).toLocaleString()}`;

/**
 * REAPI PropertySearch — the wholesale/direct-mail engine.
 *
 * Reps set location + motivation filters (equity %, absentee owner,
 * tax delinquent, code violations, MLS status), pull up to 250 matches
 * per query, plot them on a map, and one-click export to CSV for
 * mailers or dialer imports.
 */
export default function PropertySearch() {
  // ─── Admin gating ─────────────────────────────────────────────────
  // Every REAPI PropertySearch click bills real credits (a 50-row query
  // = 50 credits, a 250-row query = 250). Reps were clicking Search as
  // a reflex during onboarding, so this whole page is locked to admins
  // only — non-admins see a static lockout screen with no button to
  // click. The sidebar entry is also `adminOnly: true` so reps never
  // see the nav link in the first place; this second guard covers the
  // direct-URL case.
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;

  // ─── Filters ──────────────────────────────────────────────────────
  const [state, setStateVal] = useState("FL");
  const [city, setCity]   = useState("Cape Coral");
  const [zip, setZip]     = useState("");
  const [equityMin, setEquityMin]     = useState(50);
  const [bedsMin, setBedsMin]         = useState("");
  const [absentee, setAbsentee]       = useState(true);
  const [outOfState, setOutOfState]   = useState(false);
  const [taxLien, setTaxLien]         = useState(false);
  const [codeViolations, setCodeVio]  = useState(false);
  const [preForeclosure, setPreForec] = useState(false);
  const [freeClear, setFreeClear]     = useState(false);
  const [corporate, setCorporate]     = useState(false);
  const [mlsActive, setMlsActive]     = useState(false);
  const [mlsPending, setMlsPending]   = useState(false);
  const [mlsFailed, setMlsFailed]     = useState(false);
  const [size, setSize]               = useState(50);
  // ─── Results ──────────────────────────────────────────────────────
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});
  const [view, setView] = useState("split"); // 'split' | 'table' | 'map'
  const [selected, setSelected] = useState(null);

  const runSearch = async () => {
    if (!state && !city && !zip) {
      toast.error("Set at least state, city, or zip");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        size: Math.max(1, Math.min(250, Number(size) || 50)),
      };
      if (state) payload.state = state.toUpperCase();
      if (city)  payload.city  = city;
      if (zip)   payload.zip   = zip;
      if (equityMin) payload.equity_percent_min = Number(equityMin);
      if (bedsMin)   payload.bedrooms_min       = Number(bedsMin);
      if (absentee)       payload.absentee_owner      = true;
      if (outOfState)     payload.out_of_state_owner  = true;
      if (taxLien)        payload.tax_lien            = true;
      if (codeViolations) payload.judgment            = true;
      if (preForeclosure) payload.pre_foreclosure     = true;
      if (freeClear)      payload.free_clear          = true;
      if (corporate)      payload.corporate_owned     = true;
      if (mlsActive)      payload.mls_active          = true;
      if (mlsPending)     payload.mls_pending         = true;
      if (mlsFailed)      payload.mls_failed          = true;
      const { data } = await api.post("/properties/search", payload);
      if (!data.ok) {
        toast.error(data.error || "Search failed");
        setRows([]);
        setMeta({});
        return;
      }
      setRows(data.rows || []);
      setMeta({
        result_count: data.result_count,
        record_count: data.record_count,
      });
      toast.success(
        `${data.count} of ${(data.result_count || 0).toLocaleString()} total matches loaded`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Search failed");
    } finally {
      setBusy(false);
    }
  };

  const doExport = () => {
    if (rows.length === 0) {
      toast.info("Nothing to export yet");
      return;
    }
    exportCsv(
      rows,
      [
        { key: "address",          label: "Address" },
        { key: "city",             label: "City" },
        { key: "state",            label: "State" },
        { key: "zip",              label: "ZIP" },
        { key: "county",           label: "County" },
        { key: "owner",            label: "Owner" },
        { key: "mailing_address",  label: "Mailing Address" },
        { key: "beds",             label: "Beds" },
        { key: "baths",            label: "Baths" },
        { key: "sqft",             label: "Sqft" },
        { key: "year_built",       label: "Year Built" },
        { key: "estimated_value",  label: "Est. Value" },
        { key: "equity",           label: "Equity $" },
        { key: "equity_percent",   label: "Equity %" },
        { key: "mortgage_balance", label: "Mortgage" },
        { key: "absentee_owner",   label: "Absentee", format: (v) => v ? "Y" : "" },
        { key: "out_of_state_owner", label: "Out of State", format: (v) => v ? "Y" : "" },
        { key: "vacant",           label: "Vacant", format: (v) => v ? "Y" : "" },
        { key: "tax_lien",         label: "Tax Lien", format: (v) => v ? "Y" : "" },
        { key: "pre_foreclosure",  label: "Pre-Fcl", format: (v) => v ? "Y" : "" },
        { key: "corporate_owned",  label: "LLC-Owned", format: (v) => v ? "Y" : "" },
        { key: "free_clear",       label: "Free & Clear", format: (v) => v ? "Y" : "" },
        { key: "cash_buyer",       label: "Cash Buyer", format: (v) => v ? "Y" : "" },
        { key: "mls_active",       label: "MLS Active", format: (v) => v ? "Y" : "" },
        { key: "mls_pending",      label: "MLS Pending", format: (v) => v ? "Y" : "" },
        { key: "last_sale_date",   label: "Last Sale Date" },
        { key: "last_sale_price",  label: "Last Sale Price" },
        { key: "lat",              label: "Latitude" },
        { key: "lng",              label: "Longitude" },
      ],
      `flippilot-search-${state}-${city || zip || "all"}-${Date.now()}.csv`,
    );
    toast.success(`Exported ${rows.length} rows to CSV`);
  };

  // Center the map on the first row with lat/lng, otherwise a
  // reasonable Florida default so the map still renders on empty state.
  const first = rows.find((r) => r.lat && r.lng);
  const mapCenter = first
    ? [first.lat, first.lng]
    : [27.766279, -81.686783]; // Central Florida

  // Non-admin lockout — render a static wall with no buttons so reps
  // can't burn REAPI credits by mashing Search. Admin-only.
  if (!isAdmin) {
    return (
      <div className="p-6 max-w-3xl mx-auto" data-testid="property-search-locked">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center">
          <div className="text-[11px] uppercase tracking-widest text-amber-700 mb-2">
            / Property Search
          </div>
          <h1 className="text-2xl font-bold text-amber-900">Admin-only tool</h1>
          <p className="text-sm text-amber-800 mt-3 max-w-lg mx-auto">
            REAPI Property Search bills credits on every click. To keep the
            plan under control, only admins can run searches. Ask your
            admin to pull the list you need and drop it in your dialer
            queue.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-4" data-testid="property-search-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">
            / REAPI Property Search
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Find Motivated Sellers</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Filter REAPI&apos;s nationwide property database by equity, distress
            signals, MLS status, or owner type. Export straight to CSV for
            direct-mail or drop into the dialer queue.
          </p>
        </div>
      </div>

      {/* ─── Filter panel ─────────────────────────────────────────── */}
      <div className="rounded-2xl border border-[var(--app-line)] bg-white p-4 space-y-4" data-testid="search-filter-panel">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">State *</Label>
            <Input value={state} onChange={(e) => setStateVal(e.target.value.toUpperCase().slice(0, 2))}
                   placeholder="FL" maxLength={2}
                   data-testid="filter-state" className="mt-1" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">City</Label>
            <Input value={city} onChange={(e) => setCity(e.target.value)}
                   placeholder="Cape Coral" data-testid="filter-city" className="mt-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">ZIP</Label>
            <Input value={zip} onChange={(e) => setZip(e.target.value)}
                   placeholder="33914" maxLength={5}
                   data-testid="filter-zip" className="mt-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Equity % (min)</Label>
            <Input type="number" value={equityMin}
                   onChange={(e) => setEquityMin(e.target.value)}
                   placeholder="50" data-testid="filter-equity"
                   className="mt-1 font-mono" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Beds (min)</Label>
            <Input type="number" value={bedsMin}
                   onChange={(e) => setBedsMin(e.target.value)}
                   placeholder="any" data-testid="filter-beds"
                   className="mt-1 font-mono" />
          </div>
        </div>

        {/* Motivation-signal chips */}
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5 block">Motivation signals</Label>
          <div className="flex flex-wrap gap-2" data-testid="signal-chip-row">
            <Chip label="Absentee owner"      icon={Users}          on={absentee}       onClick={() => setAbsentee(!absentee)} testid="chip-absentee" tone="emerald" />
            <Chip label="Out of state"        icon={MapPin}         on={outOfState}     onClick={() => setOutOfState(!outOfState)} testid="chip-out-of-state" tone="sky" />
            <Chip label="Tax delinquent"      icon={AlertTriangle}  on={taxLien}        onClick={() => setTaxLien(!taxLien)} testid="chip-tax" tone="amber" />
            <Chip label="Code violations"     icon={AlertTriangle}  on={codeViolations} onClick={() => setCodeVio(!codeViolations)} testid="chip-code" tone="rose" />
            <Chip label="Pre-foreclosure"     icon={AlertTriangle}  on={preForeclosure} onClick={() => setPreForec(!preForeclosure)} testid="chip-preforec" tone="rose" />
            <Chip label="Free & clear"        icon={DollarSign}     on={freeClear}      onClick={() => setFreeClear(!freeClear)} testid="chip-freeclear" tone="emerald" />
            <Chip label="LLC-owned"           icon={Building2}      on={corporate}      onClick={() => setCorporate(!corporate)} testid="chip-corp" tone="violet" />
            <Chip label="MLS active"          icon={Home}           on={mlsActive}      onClick={() => setMlsActive(!mlsActive)} testid="chip-mls-active" tone="sky" />
            <Chip label="MLS pending"         icon={Home}           on={mlsPending}     onClick={() => setMlsPending(!mlsPending)} testid="chip-mls-pending" tone="sky" />
            <Chip label="MLS failed"          icon={Home}           on={mlsFailed}      onClick={() => setMlsFailed(!mlsFailed)} testid="chip-mls-failed" tone="rose" />
          </div>
        </div>

        {/* Run + Export bar */}
        <div className="flex flex-wrap items-center gap-3 pt-1 border-t border-slate-100">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Rows (max 250)</Label>
            <Input type="number" value={size} onChange={(e) => setSize(e.target.value)}
                   className="mt-1 w-24 font-mono" data-testid="filter-size" />
          </div>
          <Button onClick={runSearch} disabled={busy}
                  className="rounded-full h-10 mt-4"
                  data-testid="run-search-button">
            {busy ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Searching…</>
                  : <><Search size={14} className="mr-1.5" /> Search REAPI</>}
          </Button>
          <Button variant="outline" onClick={doExport} disabled={rows.length === 0}
                  className="rounded-full h-10 mt-4"
                  data-testid="export-csv-button">
            <Download size={14} className="mr-1.5" /> Export CSV
          </Button>
          {rows.length > 0 && (
            <div className="ml-auto mt-4 text-xs text-slate-500 flex items-center gap-2">
              <Badge variant="secondary" data-testid="search-result-count">
                {rows.length} / {(meta.result_count || 0).toLocaleString()} matches
              </Badge>
              <div className="hidden md:flex gap-1 rounded-full border border-slate-200 p-0.5">
                {[
                  { key: "split", label: "Split", icon: Layers },
                  { key: "table", label: "Table", icon: ListFilter },
                  { key: "map",   label: "Map",   icon: MapPin },
                ].map((v) => (
                  <button
                    key={v.key}
                    onClick={() => setView(v.key)}
                    data-testid={`view-toggle-${v.key}`}
                    className={`px-2 py-1 text-[10px] uppercase tracking-widest rounded-full inline-flex items-center gap-1 ${
                      view === v.key
                        ? "bg-slate-900 text-white"
                        : "text-slate-500 hover:text-slate-900"
                    }`}
                  >
                    <v.icon size={10} /> {v.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ─── Results ──────────────────────────────────────────────── */}
      {rows.length > 0 && (
        <div className={`grid gap-4 ${view === "split"
            ? "grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]"
            : "grid-cols-1"}`}>
          {(view === "split" || view === "table") && (
            <ResultTable rows={rows} selected={selected} onSelect={setSelected} />
          )}
          {(view === "split" || view === "map") && (
            <ResultMap rows={rows} center={mapCenter} selected={selected}
                       onSelect={setSelected} />
          )}
        </div>
      )}

      {rows.length === 0 && !busy && (
        <div className="rounded-2xl border border-dashed border-slate-200 p-12 text-center text-slate-500" data-testid="empty-state">
          <Search size={28} className="mx-auto text-slate-300 mb-2" />
          <div className="text-sm">Set your filters and hit <strong>Search REAPI</strong> to pull matches.</div>
          <div className="text-[11px] mt-1 opacity-70">Tip: for a first pass, try FL + Cape Coral + Absentee + 50% Equity.</div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────

function Chip({ label, icon: Icon, on, onClick, testid, tone = "emerald" }) {
  const palette = {
    emerald: on ? "bg-emerald-600 text-white border-transparent"
                 : "bg-white text-emerald-800 border-emerald-300 hover:bg-emerald-50",
    sky:     on ? "bg-sky-600 text-white border-transparent"
                 : "bg-white text-sky-800 border-sky-300 hover:bg-sky-50",
    amber:   on ? "bg-amber-500 text-white border-transparent"
                 : "bg-white text-amber-800 border-amber-300 hover:bg-amber-50",
    rose:    on ? "bg-rose-600 text-white border-transparent"
                 : "bg-white text-rose-800 border-rose-300 hover:bg-rose-50",
    violet:  on ? "bg-violet-600 text-white border-transparent"
                 : "bg-white text-violet-800 border-violet-300 hover:bg-violet-50",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-medium uppercase tracking-widest transition-colors ${palette[tone]}`}
    >
      <Icon size={11} />
      {label}
    </button>
  );
}

function ResultTable({ rows, selected, onSelect }) {
  return (
    <div className="rounded-2xl border border-[var(--app-line)] bg-white overflow-hidden max-h-[720px]" data-testid="result-table">
      <div className="overflow-auto max-h-[720px]">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
            <tr>
              <Th>Address</Th>
              <Th>Owner</Th>
              <Th className="text-right">Value</Th>
              <Th className="text-right">Equity %</Th>
              <Th>Signals</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r, i) => {
              const sig = [
                r.absentee_owner && "Absentee",
                r.out_of_state_owner && "OOS",
                r.tax_lien && "TaxLien",
                r.code_violations && "CodeVio",
                r.pre_foreclosure && "PreFcl",
                r.free_clear && "F&C",
                r.corporate_owned && "LLC",
                r.mls_active && "MLS·A",
                r.mls_pending && "MLS·P",
                r.vacant && "Vacant",
              ].filter(Boolean);
              return (
                <tr
                  key={r.id || i}
                  onClick={() => onSelect?.(r)}
                  data-testid={`result-row-${i}`}
                  className={`cursor-pointer hover:bg-slate-50 ${
                    selected?.id === r.id ? "bg-emerald-50" : ""
                  }`}
                >
                  <Td>
                    <div className="font-medium">{r.address}</div>
                    <div className="text-[10px] text-slate-400">
                      {r.beds}br/{r.baths}ba · {r.sqft ? r.sqft.toLocaleString() : "—"} sqft
                    </div>
                  </Td>
                  <Td className="text-slate-600 max-w-[160px] truncate" title={r.owner}>{r.owner || "—"}</Td>
                  <Td className="text-right font-mono">{money(r.estimated_value)}</Td>
                  <Td className="text-right font-mono">{r.equity_percent != null ? `${Math.round(r.equity_percent)}%` : "—"}</Td>
                  <Td>
                    <div className="flex flex-wrap gap-1">
                      {sig.slice(0, 4).map((s) => (
                        <span key={s} className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 uppercase tracking-wider">
                          {s}
                        </span>
                      ))}
                      {sig.length > 4 && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">+{sig.length - 4}</span>
                      )}
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`text-left text-[10px] uppercase tracking-widest text-slate-500 font-semibold px-3 py-2 ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-3 py-2 align-top ${className}`}>{children}</td>
);

function ResultMap({ rows, center, selected, onSelect }) {
  const pins = rows.filter((r) => r.lat && r.lng);
  return (
    <div className="rounded-2xl border border-[var(--app-line)] bg-white overflow-hidden" data-testid="result-map">
      <MapContainer
        center={center}
        zoom={pins.length > 0 ? 11 : 8}
        scrollWheelZoom
        style={{ height: 720, width: "100%" }}
      >
        <TileLayer
          attribution='&copy; OpenStreetMap contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {pins.map((r, i) => (
          <Marker
            key={r.id || i}
            position={[r.lat, r.lng]}
            eventHandlers={{ click: () => onSelect?.(r) }}
          >
            <Popup>
              <div className="text-xs space-y-1 min-w-[220px]">
                <div className="font-semibold">{r.address}</div>
                <div className="text-slate-600">{r.owner || "—"}</div>
                <div>
                  {money(r.estimated_value)} · {r.equity_percent != null ? `${Math.round(r.equity_percent)}% equity` : "—"}
                </div>
                <div className="text-[10px] text-slate-500">
                  {r.beds}br/{r.baths}ba · {r.sqft ? r.sqft.toLocaleString() : "—"} sqft
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
